import { Link } from "react-router-dom";
import { MessageCircle, Phone, ArrowRight } from "lucide-react";
import { CONTACT } from "@/data/site";

export const Eyebrow = ({ children, light = false }) => (
  <span className={`eyebrow ${light ? "text-white/60" : "text-brand-green"}`}>
    {children}
  </span>
);

export const SectionHeading = ({ eyebrow, title, intro, light = false, align = "left" }) => (
  <div className={`max-w-2xl ${align === "center" ? "mx-auto text-center" : ""}`}>
    {eyebrow && <Eyebrow light={light}>{eyebrow}</Eyebrow>}
    <h2
      className={`mt-4 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-tight ${
        light ? "text-white" : "text-brand-ink"
      }`}
    >
      {title}
    </h2>
    {intro && (
      <p className={`mt-5 text-base md:text-lg leading-relaxed ${light ? "text-white/70" : "text-brand-slate"}`}>
        {intro}
      </p>
    )}
  </div>
);

export const PrimaryButton = ({ to, href, children, testid, className = "", ...rest }) => {
  const cls = `inline-flex items-center gap-2 rounded-full bg-brand-green px-8 py-4 text-sm font-medium text-white transition-all duration-300 hover:bg-brand-greenDark hover:-translate-y-0.5 ${className}`;
  if (to) return <Link to={to} data-testid={testid} className={cls} {...rest}>{children}</Link>;
  return <a href={href} data-testid={testid} className={cls} {...rest}>{children}</a>;
};

export const SecondaryButton = ({ to, href, children, testid, className = "", ...rest }) => {
  const cls = `inline-flex items-center gap-2 rounded-full border border-brand-purple px-8 py-4 text-sm font-medium text-brand-purple transition-all duration-300 hover:bg-brand-purple hover:text-white hover:-translate-y-0.5 ${className}`;
  if (to) return <Link to={to} data-testid={testid} className={cls} {...rest}>{children}</Link>;
  return <a href={href} data-testid={testid} className={cls} {...rest}>{children}</a>;
};

export const ConversionRow = ({ testidPrefix = "cta", variant = "order" }) => {
  const isCatering = variant === "catering";
  const waHref = isCatering ? CONTACT.whatsappCateringHref : CONTACT.whatsappOrderHref;
  return (
    <div className="flex flex-wrap gap-3">
      <PrimaryButton href={waHref} target="_blank" rel="noopener noreferrer" testid={`${testidPrefix}-whatsapp`}>
        <MessageCircle size={18} /> {isCatering ? "Enquire on WhatsApp" : "Order on WhatsApp"}
      </PrimaryButton>
      <SecondaryButton href={CONTACT.phoneHref} testid={`${testidPrefix}-call`}>
        <Phone size={18} /> Call Us
      </SecondaryButton>
    </div>
  );
};

export const PageHero = ({ eyebrow, title, intro, image, imageAlt }) => (
  <section className="relative pt-36 lg:pt-44 pb-16 lg:pb-24 overflow-hidden">
    <div className="container-px mx-auto max-w-[1400px] grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
      <div className="animate-fade-up">
        <Eyebrow>{eyebrow}</Eyebrow>
        <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] text-brand-ink">
          {title}
        </h1>
        {intro && <p className="mt-6 max-w-xl text-base md:text-lg leading-relaxed text-brand-slate">{intro}</p>}
      </div>
      <div className="img-zoom-wrap relative overflow-hidden rounded-[2rem] aspect-[4/3] animate-fade-in">
        <img src={image} alt={imageAlt} className="img-zoom h-full w-full object-cover" />
      </div>
    </div>
  </section>
);

export const TextLink = ({ to, children, testid }) => (
  <Link
    to={to}
    data-testid={testid}
    className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-green group"
  >
    {children}
    <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
  </Link>
);
